// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Flight-flag summary pipeline: derives the per-flight badge state
// (review / watch / clear / not-analyzed / loading) from an analysis
// run, renders the badge markup, and hydrates summaries lazily from
// either the database flag-summary endpoint or per-flight analysis-run
// fetches. The four mutable Maps + the databaseFlightFlagIndexPromise
// live in app.js, passed in below.
//
// On top of the in-memory cache, per-aircraft summary results are
// mirrored to localStorage so reloading the renderer can repaint the
// Flags column instantly from cache; a background fetch still runs
// once per aircraft per session to revalidate the cached values.

import {
  egtRhythmAmplitudeSeverity,
  egtRhythmMetricSeverity,
} from "../analysis/detectors/egt-rhythm.js";

const FLAG_SUMMARY_STORAGE_PREFIX = "aeroinsight:flightFlagSummaries:v6:";
const CHT_HIGH_THRESHOLD_F = 400;

function legacyValveFlagAmplitude(flag) {
  const explicit = Number(flag?.amplitudeF ?? flag?.amplitude);
  if (Number.isFinite(explicit)) return explicit;
  const noteMatch = String(flag?.note ?? "").match(/\b(\d+(?:\.\d+)?)\s*F\b/i);
  const noteAmplitude = Number(noteMatch?.[1]);
  return Number.isFinite(noteAmplitude) ? noteAmplitude : null;
}

function egtRhythmFlagSeverity({ metric = null, finding = null, legacyFlag = null } = {}) {
  const metricSeverity = egtRhythmMetricSeverity(metric);
  if (metricSeverity === "warning") return "review";
  if (metricSeverity === "watch") return "watch";
  const findingSeverity = finding?.details?.amplitudeSeverity;
  if (findingSeverity === "warning") return "review";
  if (findingSeverity === "watch") return "watch";
  const findingAmplitude = Number(finding?.details?.amplitudeF);
  if (egtRhythmAmplitudeSeverity(findingAmplitude) === "warning") return "review";
  return egtRhythmAmplitudeSeverity(legacyValveFlagAmplitude(legacyFlag)) === "warning"
    ? "review"
    : "watch";
}

function flagSummaryStorage() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function flagSummaryStorageKey(aircraftId) {
  return `${FLAG_SUMMARY_STORAGE_PREFIX}${aircraftId}`;
}

export function clearPersistedAircraftFlightFlagSummaries(aircraftId) {
  const store = flagSummaryStorage();
  if (!store || !aircraftId) return;
  try {
    store.removeItem(flagSummaryStorageKey(aircraftId));
  } catch {
    // non-fatal: stub storage may not implement removeItem
  }
}

export function createFlightFlagSummary({
  flightFlagSummaryCache,
  flightFlagSummaryPending,
  aircraftFlightFlagSummaryIndexPromises,
  getDatabaseFlightFlagIndexPromise,
  setDatabaseFlightFlagIndexPromise,
  aircraftFlightsHydrationBatchSize,
  databaseAircraftEndpoint,
  databaseAircraftSummaryEndpoint,
  databaseAircraftFlightFlagSummariesEndpoint,
  databaseAircraftLabel,
  disabledReviewMonitorKeys,
  selectedAircraftWorkspace,
  escapeHtml,
  isSupportedFlight,
  valveFlagForFlight,
  renderAircraftFlightsPanel,
  getSelectedAircraftPanel,
}) {
  const primedAircraftIds = new Set();
  const revalidatedAircraftIds = new Set();
  const invalidatedAircraftVersions = new Map();

  function invalidatedAircraftVersion(aircraftId) {
    return invalidatedAircraftVersions.get(aircraftId) ?? 0;
  }

  function invalidateAircraftVersion(aircraftId) {
    if (!aircraftId) return;
    invalidatedAircraftVersions.set(aircraftId, invalidatedAircraftVersion(aircraftId) + 1);
  }

  function invalidateAircraftFlightFlagSummaries(aircraftId, entries = []) {
    if (!aircraftId) return;
    invalidateAircraftVersion(aircraftId);
    primedAircraftIds.delete(aircraftId);
    revalidatedAircraftIds.delete(aircraftId);
    aircraftFlightFlagSummaryIndexPromises.delete(aircraftId);
    clearPersistedAircraftFlightFlagSummaries(aircraftId);
    entries.forEach((entry) => {
      const entryId = String(entry?.id ?? "").trim();
      const databaseFlightId = String(entry?.database?.flightId ?? "").trim();
      if (entryId) {
        flightFlagSummaryCache.delete(entryId);
        flightFlagSummaryPending.delete(entryId);
      }
      if (databaseFlightId) {
        flightFlagSummaryCache.delete(databaseFlightId);
        flightFlagSummaryPending.delete(databaseFlightId);
      }
    });
  }

  function persistAircraftSummaries(aircraftId, summaries) {
    const store = flagSummaryStorage();
    if (!store || !aircraftId || !(summaries instanceof Map)) return;
    try {
      const entries = {};
      summaries.forEach((value, key) => {
        if (key) entries[key] = value;
      });
      store.setItem(flagSummaryStorageKey(aircraftId), JSON.stringify({
        savedAt: Date.now(),
        summaries: entries,
      }));
    } catch (error) {
      // localStorage quota or serialization failure — drop and refetch
      // next time, but log so silent quota exhaustion is diagnosable
      // from the console instead of inferred from an unexplained
      // re-fetch every reload.
      console.warn(
        `aeroinsight: could not persist flight flag summaries for aircraft ${aircraftId} — `
        + `${error?.message ?? String(error)}. Subsequent loads will refetch from the server.`,
      );
    }
  }

  function primeAircraftSummariesFromStorage(aircraftId) {
    if (!aircraftId || primedAircraftIds.has(aircraftId)) return 0;
    primedAircraftIds.add(aircraftId);
    const store = flagSummaryStorage();
    if (!store) return 0;
    let payload = null;
    try {
      const raw = store.getItem(flagSummaryStorageKey(aircraftId));
      if (!raw) return 0;
      payload = JSON.parse(raw);
    } catch {
      return 0;
    }
    const entries = payload?.summaries;
    if (!entries || typeof entries !== "object") return 0;
    let added = 0;
    Object.entries(entries).forEach(([flightId, summary]) => {
      if (!flightId || !summary || typeof summary !== "object") return;
      if (flightFlagSummaryCache.has(flightId)) return;
      flightFlagSummaryCache.set(flightId, summary);
      added += 1;
    });
    return added;
  }

  function noSavedResultSummary() {
    return {
      state: "not-analyzed",
      label: "—",
      title: "No saved CHT or EGT Rhythm result.",
    };
  }

  function cacheSummaryForEntry(entry, summary) {
    const entryId = String(entry?.id ?? "").trim();
    const databaseFlightId = String(entry?.database?.flightId ?? "").trim();
    if (entryId) flightFlagSummaryCache.set(entryId, summary);
    if (databaseFlightId) flightFlagSummaryCache.set(databaseFlightId, summary);
  }

  function cachedSummaryForEntry(entry) {
    const entryId = String(entry?.id ?? "").trim();
    const databaseFlightId = String(entry?.database?.flightId ?? "").trim();
    const cached = (entryId && flightFlagSummaryCache.get(entryId))
      || (databaseFlightId && flightFlagSummaryCache.get(databaseFlightId))
      || null;
    const currentAnalysisRunId = String(entry?.database?.analysisRunId ?? "").trim();
    const cachedAnalysisRunId = String(cached?.analysisRunId ?? "").trim();
    if (cached && currentAnalysisRunId && cachedAnalysisRunId && cachedAnalysisRunId !== currentAnalysisRunId) {
      if (entryId) flightFlagSummaryCache.delete(entryId);
      if (databaseFlightId) flightFlagSummaryCache.delete(databaseFlightId);
      return null;
    }
    if (cached && entryId && !flightFlagSummaryCache.has(entryId)) {
      flightFlagSummaryCache.set(entryId, cached);
    }
    if (cached && databaseFlightId && !flightFlagSummaryCache.has(databaseFlightId)) {
      flightFlagSummaryCache.set(databaseFlightId, cached);
    }
    return cached;
  }

  function flightFlagSummary(entry) {
    const compatibilityStatus = entry?.database?.compatibilityStatus?.status;
    const cached = cachedSummaryForEntry(entry);
    if (cached) return cached;
    const aircraftId = entry?.database?.aircraftId;
    if (aircraftId && !primedAircraftIds.has(aircraftId)) {
      primeAircraftSummariesFromStorage(aircraftId);
      const reCached = cachedSummaryForEntry(entry);
      if (reCached) return reCached;
    }
    const persistedFlagAnalysis = entry?.database?.flagAnalysis;
    if (persistedFlagAnalysis && entry?.database?.analysisRunId) {
      const summary = flightFlagSummaryFromAnalysisRun({
        id: entry.database.analysisRunId,
        findings: persistedFlagAnalysis.findings ?? [],
        score_summary: { metrics: persistedFlagAnalysis.metrics ?? [] },
      }, entry?.aircraft);
      cacheSummaryForEntry(entry, summary);
      return summary;
    }
    if (entry?.id && flightFlagSummaryPending.has(entry.id)) return { state: "loading", label: "Checking..." };
    if (["analysis_update_available", "parser_update_available", "incompatible"].includes(compatibilityStatus)) {
      if (isSupportedFlight(entry) && entry?.database?.analysisRunId) return { state: "loading", label: "Checking..." };
      return noSavedResultSummary();
    }
    const valveFlag = valveFlagForFlight(entry);
    if (valveFlag) {
      const score = Number(valveFlag.score);
      const severity = egtRhythmFlagSeverity({ legacyFlag: valveFlag });
      const egtRhythm = {
        score: Number.isFinite(score) ? score : null,
        severity,
        statusLabel: severity === "review" ? "Warning" : "Watch",
        title: "EGT Rhythm",
        label: "EGT Rhythm",
      };
      return {
        state: "ready",
        reviewCount: egtRhythm.severity === "review" ? 1 : 0,
        watchCount: egtRhythm.severity === "watch" ? 1 : 0,
        egtRhythm,
        titles: [valveFlag.note ? `EGT Rhythm: ${valveFlag.note}` : "EGT Rhythm"],
        label: egtRhythm.label,
      };
    }
    if (isSupportedFlight(entry) && entry?.database?.analysisRunId) return { state: "loading", label: "Checking..." };
    if (!isSupportedFlight(entry)) return { state: "unsupported", label: "Unsupported" };
    return noSavedResultSummary();
  }

  function scoreSummaryMetric(analysisRun, key) {
    const metrics = Array.isArray(analysisRun?.score_summary?.metrics)
      ? analysisRun.score_summary.metrics
      : [];
    return metrics.find((metric) => metric?.key === key) ?? null;
  }

  function highChtSummaryFromAnalysisRun(analysisRun, findings, disabledKeys) {
    if (disabledKeys.has("cht")) return null;
    const storedFinding = findings.find((finding) => finding?.key === "cht-peak-high");
    const metric = scoreSummaryMetric(analysisRun, "cht");
    const storedPeak = Number(storedFinding?.details?.peakF);
    const metricPeak = Number(metric?.details?.max);
    const peakF = [storedPeak, metricPeak].filter(Number.isFinite).sort((a, b) => b - a)[0] ?? null;
    if (!Number.isFinite(peakF) || peakF <= CHT_HIGH_THRESHOLD_F) return null;
    const title = storedFinding?.summary
      ?? storedFinding?.title
      ?? `Peak CHT exceeded ${CHT_HIGH_THRESHOLD_F} F`;
    return {
      peakF,
      thresholdF: CHT_HIGH_THRESHOLD_F,
      title,
    };
  }

  function egtRhythmSummaryFromFindings(analysisRun, findings, disabledKeys) {
    if (disabledKeys.has("valve")) return null;
    const finding = findings.find((item) => {
      if (item?.key !== "valve") return false;
      const score = Number(item?.score);
      return item?.severity === "review" || item?.severity === "watch" || (Number.isFinite(score) && score < 88);
    });
    if (!finding) return null;
    const metric = scoreSummaryMetric(analysisRun, "valve");
    const score = Number(finding.score);
    const severity = egtRhythmFlagSeverity({ metric, finding });
    return {
      score: Number.isFinite(score) ? score : null,
      severity,
      statusLabel: severity === "review" ? "Warning" : "Watch",
      title: finding.summary ?? finding.title ?? "EGT Rhythm",
      label: "EGT Rhythm",
    };
  }

  function flightFlagSummaryFromAnalysisRun(analysisRun, aircraft = selectedAircraftWorkspace()) {
    const disabledKeys = disabledReviewMonitorKeys(aircraft);
    const findings = Array.isArray(analysisRun?.findings) ? analysisRun.findings : [];
    const highCht = highChtSummaryFromAnalysisRun(analysisRun, findings, disabledKeys);
    const egtRhythm = egtRhythmSummaryFromFindings(analysisRun, findings, disabledKeys);
    const reviewCount = (highCht ? 1 : 0) + (egtRhythm?.severity === "review" ? 1 : 0);
    const watchCount = egtRhythm?.severity === "watch" ? 1 : 0;
    const titles = [
      highCht?.title,
      egtRhythm?.title,
    ].filter(Boolean);
    const labelParts = [];
    if (highCht) {
      labelParts.push(highCht.peakF ? `CHT ${Math.round(highCht.peakF)}°F` : "CHT");
    }
    if (egtRhythm) labelParts.push(egtRhythm.label);
    return {
      state: "ready",
      reviewCount,
      watchCount,
      highCht,
      egtRhythm,
      titles,
      label: labelParts.length ? labelParts.join(" · ") : "None",
      analysisRunId: analysisRun?.id ?? analysisRun?.analysisRunId ?? null,
    };
  }

  function flightFlagSummaryMarkup(summary, entry = null) {
    const state = summary?.state ?? "not-analyzed";
    const title = summary?.titles?.length
      ? summary.titles.join(" · ")
      : summary?.label ?? "";
    if (state === "ready") {
      const highCht = summary?.highCht ?? null;
      const egtRhythm = summary?.egtRhythm ?? null;
      const highChtLabel = highCht
        ? (Number.isFinite(highCht.peakF) ? `CHT ${Math.round(highCht.peakF)}°F` : "CHT")
        : "";
      const highChtTitle = highCht?.title ?? "High CHT";
      const egtRhythmTitle = egtRhythm
        ? `${egtRhythm.title ?? "EGT Rhythm"} · ${egtRhythm.statusLabel ?? (egtRhythm.severity === "review" ? "Warning" : "Watch")}`
        : "EGT Rhythm";
      const egtRhythmLabel = "EGT Rhythm";
      const egtRhythmClass = egtRhythm?.severity === "review"
        ? "aircraft-flight-flags-review"
        : "aircraft-flight-flags-watch";
      const badges = [
        highCht
          ? `<span class="aircraft-flight-flags aircraft-flight-flags-high-cht" title="${escapeHtml(highChtTitle)}">${escapeHtml(highChtLabel)}</span>`
          : "",
        egtRhythm
          ? `<span class="aircraft-flight-flags aircraft-flight-flags-egt-rhythm ${egtRhythmClass}" title="${escapeHtml(egtRhythmTitle)}">${escapeHtml(egtRhythmLabel)}</span>`
          : "",
      ].filter(Boolean).join("");
      const resultMarkup = badges ? `<span class="aircraft-flight-flags-stack">${badges}</span>` : `
        <span class="aircraft-flight-flags aircraft-flight-flags-clear" title="${escapeHtml(title)}">
          None
        </span>
      `;
      const compatibility = entry?.database?.compatibilityStatus;
      const previousAnalysis = compatibility?.analysisUpdate?.available === true
        || compatibility?.parserUpdate?.available === true
        || ["analysis_update_available", "parser_update_available", "incompatible"].includes(compatibility?.status);
      if (!previousAnalysis) return resultMarkup;
      return `<span class="aircraft-flight-flags-saved">
        ${resultMarkup}
        <span class="aircraft-flight-flags-previous" title="Saved results from a previous analysis may differ from the current flight review. Update this flight to refresh its saved results.">Previous analysis</span>
      </span>`;
    }
    if (state === "not-analyzed") {
      return '<span class="aircraft-flight-flags aircraft-flight-flags-empty" aria-label="No current analysis">—</span>';
    }
    if (state === "analysis-required") {
      return '<span class="aircraft-flight-flags aircraft-flight-flags-empty" aria-label="No saved CHT or EGT Rhythm result">—</span>';
    }
    return `
      <span class="aircraft-flight-flags aircraft-flight-flags-muted" title="${escapeHtml(title)}">
        ${escapeHtml(summary?.label ?? "—")}
      </span>
    `;
  }

  function flightFlagIdentityKeys(entry) {
    const keys = [];
    const add = (kind, value) => {
      const normalized = String(value ?? "").trim();
      if (normalized) keys.push(`${kind}:${normalized}`);
    };
    add("hash", entry?.sourceHash);
    add("path", entry?.path);
    add("filename", String(entry?.path ?? "").split("/").pop());
    add("aircraft-title", [entry?.aircraft, entry?.title].filter(Boolean).join("::"));
    return keys;
  }

  async function databaseFlightFlagIndex() {
    const existing = getDatabaseFlightFlagIndexPromise();
    if (existing) return existing;
    const promise = (async () => {
      const aircraftResponse = await fetch(databaseAircraftSummaryEndpoint(), { cache: "no-store" });
      if (!aircraftResponse.ok) return new Map();
      const payload = await aircraftResponse.json();
      const aircraftRows = Array.isArray(payload.aircraft) ? payload.aircraft : [];
      const index = new Map();
      await Promise.all(aircraftRows.map(async (aircraft) => {
        const aircraftId = aircraft?.id;
        if (!aircraftId) return;
        const flightsResponse = await fetch(`${databaseAircraftEndpoint}/${encodeURIComponent(aircraftId)}/flights`, { cache: "no-store" });
        if (!flightsResponse.ok) return;
        const flightsPayload = await flightsResponse.json();
        const aircraftLabel = databaseAircraftLabel(aircraft);
        (flightsPayload.flights ?? []).forEach((flight) => {
          const record = {
            flightId: flight?.id,
            analysisRunId: flight?.current_analysis_run_id ?? null,
          };
          if (!record.flightId || !record.analysisRunId) return;
          flightFlagIdentityKeys({
            aircraft: aircraftLabel,
            title: flight?.display_title,
            path: flight?.storage_key,
            original_filename: flight?.original_filename,
            sourceHash: flight?.source_sha256,
          }).forEach((key) => index.set(key, record));
          if (flight?.original_filename) index.set(`filename:${String(flight.original_filename).trim()}`, record);
        });
      }));
      return index;
    })().catch(() => new Map());
    setDatabaseFlightFlagIndexPromise(promise);
    return promise;
  }

  async function hydrateAircraftFlightFlagSummary(entry) {
    const databaseFlightId = entry?.database?.flightId;
    const analysisRunId = entry?.database?.analysisRunId;
    let flightId = analysisRunId ? databaseFlightId : null;
    if (!flightId) {
      const index = await databaseFlightFlagIndex();
      const match = flightFlagIdentityKeys(entry).map((key) => index.get(key)).find(Boolean);
      flightId = match?.flightId ?? null;
    }
    if (!flightId) {
      return noSavedResultSummary();
    }
    const response = await fetch(`/api/flights/${encodeURIComponent(flightId)}/analysis-run`, { cache: "no-store" });
    const payload = response.ok ? await response.json() : null;
    return flightFlagSummaryFromAnalysisRun(payload?.analysisRun, entry?.aircraft);
  }

  async function hydrateDatabaseAircraftFlightFlagSummaries(targets = [], aircraft = selectedAircraftWorkspace()) {
    const aircraftId = targets.find((entry) => entry?.database?.aircraftId)?.database?.aircraftId;
    if (!aircraftId) return false;
    const startedAircraftVersion = invalidatedAircraftVersion(aircraftId);
    if (!aircraftFlightFlagSummaryIndexPromises.has(aircraftId)) {
      const promise = fetch(databaseAircraftFlightFlagSummariesEndpoint(aircraftId), { cache: "no-store" })
        .then(async (response) => {
          if (!response.ok) throw new Error(`Flight flag summary API HTTP ${response.status}`);
          const payload = await response.json();
          if (invalidatedAircraftVersion(aircraftId) !== startedAircraftVersion) return null;
          const summaries = new Map();
          (payload.summaries ?? []).forEach((item) => {
            if (!item?.flightId) return;
            summaries.set(item.flightId, flightFlagSummaryFromAnalysisRun({
              id: item.analysisRunId ?? null,
              findings: item.findings ?? [],
              score_summary: item.score_summary ?? {},
            }, aircraft));
          });
          persistAircraftSummaries(aircraftId, summaries);
          revalidatedAircraftIds.add(aircraftId);
          return summaries;
        })
        .catch(() => null);
      aircraftFlightFlagSummaryIndexPromises.set(aircraftId, promise);
    }
    const summaries = await aircraftFlightFlagSummaryIndexPromises.get(aircraftId);
    if (!summaries) return false;
    if (invalidatedAircraftVersion(aircraftId) !== startedAircraftVersion) return false;
    targets.forEach((entry) => {
      const summary = summaries.get(entry?.database?.flightId)
        ?? noSavedResultSummary();
      if (entry?.database && summary.analysisRunId) {
        entry.database.analysisRunId = summary.analysisRunId;
      }
      cacheSummaryForEntry(entry, summary);
    });
    return true;
  }

  function hydrateAircraftFlightFlagSummaries(flights = []) {
    const supportedFlights = flights
      .filter((entry) => isSupportedFlight(entry) && entry?.id);
    supportedFlights.forEach((entry) => {
      const aircraftId = entry?.database?.aircraftId;
      if (aircraftId) primeAircraftSummariesFromStorage(aircraftId);
    });
    const targets = supportedFlights
      .filter((entry) => (
        (entry?.database?.analysisRunId || !entry?.database?.flightId)
        && !entry?.database?.flagAnalysis
        && !flightFlagSummaryCache.has(entry.id)
        && !flightFlagSummaryPending.has(entry.id)
      ));
    // New analysis runs carry their compact flag payload on the flight-list
    // response, so they are already authoritative and need no aircraft-wide
    // sidecar hydration. Keep the fallback only for historical rows created
    // before that inline contract existed.
    const legacyHydrationFlights = supportedFlights.filter((entry) => (
      entry?.database?.analysisRunId && !entry?.database?.flagAnalysis
    ));
    const revalidateAircraftIds = Array.from(new Set(legacyHydrationFlights
      .map((entry) => entry?.database?.aircraftId)
      .filter((id) => id && !revalidatedAircraftIds.has(id) && !aircraftFlightFlagSummaryIndexPromises.has(id))));
    if (!targets.length && !revalidateAircraftIds.length) return;

    const fetchTargets = targets.length
      ? targets
      : legacyHydrationFlights.filter((entry) => revalidateAircraftIds.includes(entry?.database?.aircraftId));

    const batch = (async () => {
      if (fetchTargets.some((entry) => entry?.database?.aircraftId)) {
        const hydrated = await hydrateDatabaseAircraftFlightFlagSummaries(fetchTargets, selectedAircraftWorkspace());
        if (hydrated) {
          targets.forEach((entry) => flightFlagSummaryPending.delete(entry.id));
          return;
        }
      }
      for (let index = 0; index < targets.length; index += aircraftFlightsHydrationBatchSize) {
        const chunk = targets.slice(index, index + aircraftFlightsHydrationBatchSize);
        await Promise.all(chunk.map(async (entry) => {
          const aircraftId = entry?.database?.aircraftId;
          const startedAircraftVersion = invalidatedAircraftVersion(aircraftId);
          try {
            const summary = await hydrateAircraftFlightFlagSummary(entry);
            if (invalidatedAircraftVersion(aircraftId) !== startedAircraftVersion) return;
            cacheSummaryForEntry(entry, summary);
          } catch {
            if (invalidatedAircraftVersion(aircraftId) !== startedAircraftVersion) return;
            cacheSummaryForEntry(
              entry,
              noSavedResultSummary(),
            );
          } finally {
            flightFlagSummaryPending.delete(entry.id);
          }
        }));
      }
    })().finally(() => {
      if (getSelectedAircraftPanel() === "flights" || document.querySelector("#aircraftFlightsPanel")?.classList.contains("is-active")) {
        renderAircraftFlightsPanel(selectedAircraftWorkspace());
      }
    });

    targets.forEach((entry) => {
      flightFlagSummaryPending.set(entry.id, batch);
    });
  }

  return {
    flightFlagSummary,
    flightFlagSummaryFromAnalysisRun,
    flightFlagSummaryMarkup,
    flightFlagIdentityKeys,
    invalidateAircraftFlightFlagSummaries,
    databaseFlightFlagIndex,
    hydrateAircraftFlightFlagSummary,
    hydrateDatabaseAircraftFlightFlagSummaries,
    hydrateAircraftFlightFlagSummaries,
  };
}
